import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { CHANGELOG } from "../changelog";

type Props = {
  open: boolean;
  anchor: HTMLElement | null;
  onClose: () => void;
};

export function ChangelogPopover({ open, anchor, onClose }: Props) {
  const popRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Mount off-screen first so we can measure the popover's real (content-
  // dependent) height, then place it for real. Using a hardcoded height
  // here previously meant a scrolled-down footer (rect.top near the top of
  // the viewport) could push the "open upward" math negative and clamp the
  // popover to the very top of the screen, nowhere near the button that
  // opened it.
  useEffect(() => {
    if (!open || !anchor) {
      setPos(null);
      return;
    }
    setPos({ top: -9999, left: -9999 });
  }, [open, anchor]);

  useLayoutEffect(() => {
    if (!open || !anchor || !popRef.current) return;
    if (pos && pos.top !== -9999) return; // already placed for real
    const anchorRect = anchor.getBoundingClientRect();
    const popRect = popRef.current.getBoundingClientRect();
    const width = popRect.width || 320;
    const height = popRect.height || 320;

    let left = anchorRect.left;
    if (left + width > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - width - 12);
    }

    // Prefer opening above the anchor (the footer sits at the bottom of
    // the page), but flip below it when there isn't enough room above.
    const top =
      anchorRect.top - height - 8 >= 12
        ? anchorRect.top - height - 8
        : Math.min(anchorRect.bottom + 8, window.innerHeight - height - 12);

    setPos({ top, left });
  }, [open, anchor, pos]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current && popRef.current.contains(t)) return;
      if (anchor && anchor.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, anchor, onClose]);

  if (!open || !pos) return null;

  const entries = CHANGELOG.slice(0, 5);
  const measuring = pos.top === -9999;

  return (
    <div
      ref={popRef}
      className="changelog-popover"
      role="dialog"
      aria-label="Changelog"
      style={{ top: pos.top, left: pos.left, opacity: measuring ? 0 : 1 }}
    >
      <div className="changelog-popover-head">What's new</div>
      <div className="changelog-popover-body">
        {entries.map((e) => (
          <div key={e.version} className="changelog-entry">
            <div className="changelog-entry-head">
              <span className="changelog-entry-version">v{e.version}</span>
              <span className="changelog-entry-date">{e.date}</span>
            </div>
            <ul className="changelog-entry-list">
              {e.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
